/**
 * 1.3.0 Administration → Media portal → Companies & users (P1): the production companies that deliver through the
 * portal, and for the one chosen its users (invite, resend the invitation, disable, reset two-step, sign out
 * everywhere, personal data) and its upload history.
 */
import { useState } from 'react';
import { Building2, Copy, Plus, UserPlus, Users } from 'lucide-react';
import type { CompanyTwoStepPolicy, PortalCompanyDTO, PortalUserDTO } from '@prism/contracts';
import { api, errorDetail } from '../../../api/client';
import { usePortalAdminRefresh, usePortalCompanies, usePortalCompanyUploads, usePortalSettings, usePortalUsers, type PortalInviteResult } from '../../../api/hooks/portalAdmin';
import { Modal } from '../../../components/Modal';
import { TableScroll } from '../../../components/TableScroll';
import { toast } from '../../../components/Toasts';
import { copy } from '../../../lib/clipboard';
import { bytes, relative } from '../../../lib/format';
import { QuotaUsageBars } from './QuotaUsage';
import { CompanyActivity } from './CompanyActivity';
import { fromGib, toGib } from './shared';

const POLICY_LABEL: Record<CompanyTwoStepPolicy, string> = { inherit: 'As the portal', required: 'Required', optional: 'Optional' };

export function PortalCompanies() {
  const companies = usePortalCompanies();
  const settings = usePortalSettings().data;
  const [editing, setEditing] = useState<PortalCompanyDTO | 'new' | null>(null);
  const [chosenId, setChosenId] = useState<string | null>(null);
  const items = companies.data?.items ?? [];
  const chosen = items.find((c) => c.id === chosenId) ?? null;
  const portalPolicy = settings?.twoStepPolicy === 'optional' ? 'optional' : 'required';
  return (
    <>
      <section className="panel-card" data-testid="portal-companies">
        <div className="portal-card-head">
          <h3><Building2 size={16} color="var(--accent-strong)" /> Companies</h3>
          <button className="btn sm" onClick={() => setEditing('new')} data-testid="portal-add-company"><Plus size={14} /> New company</button>
        </div>
        <p className="muted portal-lede">Production companies that deliver or upload through the portal. Their users sign in on the portal’s own address only; PRISM staff invite them. Choose a company to see its users and uploads.</p>
        <TableScroll>
          <table className="table compact">
            <thead><tr><th>Name</th><th>Contact</th><th>Users</th><th>Two-step sign-in</th><th>Usage</th><th>Status</th><th /></tr></thead>
            <tbody>
              {companies.data && !items.length && <tr><td colSpan={7} className="muted">No companies yet. Add the first one to invite its people.</td></tr>}
              {items.map((c) => (
                <tr key={c.id} className={`${c.disabled ? 'faint' : ''} ${c.id === chosenId ? 'portal-row-chosen' : ''}`} data-company={c.name}>
                  <td className="wrap" style={{ minWidth: 160 }}>
                    <button className="link-button" onClick={() => setChosenId(c.id === chosenId ? null : c.id)} data-testid="portal-company-open">{c.name}</button>
                    {c.notes && <div className="faint portal-clamp">{c.notes}</div>}
                  </td>
                  <td className="wrap" style={{ minWidth: 140 }}>{c.contactName ?? '—'}{c.contactEmail && <div className="faint break-any">{c.contactEmail}</div>}</td>
                  <td className="nowrap">{c.userCount}</td>
                  <td className="nowrap">{c.twoStepPolicy === 'inherit' ? `As the portal (${portalPolicy})` : POLICY_LABEL[c.twoStepPolicy]}</td>
                  {/* 1.3.1 (Q1, QuotaUsage.tsx). */}
                  <td className="nowrap" data-testid="portal-company-usage"><QuotaUsageBars company={c} /></td>
                  <td className="nowrap">{c.disabled ? <span className="pill danger">Disabled</span> : <span className="pill accent">Active</span>}</td>
                  <td className="actions"><div>
                    <button className="btn ghost sm" onClick={() => setChosenId(c.id)}>Users</button>
                    <button className="btn ghost sm" onClick={() => setEditing(c)} data-testid="portal-edit-company">Edit</button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      </section>
      {chosen && <CompanyUsers key={chosen.id} company={chosen} />}
      {chosen && <CompanyUploads company={chosen} />}
      {/* 1.3.1 (Q1, CompanyActivity.tsx). */}
      {chosen && <CompanyActivity key={`activity-${chosen.id}`} company={chosen} />}
      {editing && <CompanyModal company={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSaved={(c) => { setEditing(null); setChosenId(c.id); }} />}
    </>
  );
}

function CompanyModal({ company, onClose, onSaved }: { company: PortalCompanyDTO | null; onClose: () => void; onSaved: (c: PortalCompanyDTO) => void }) {
  const refresh = usePortalAdminRefresh();
  const [v, setV] = useState({
    name: company?.name ?? '', contactName: company?.contactName ?? '', contactEmail: company?.contactEmail ?? '', notes: company?.notes ?? '',
    twoStepPolicy: company?.twoStepPolicy ?? 'inherit' as CompanyTwoStepPolicy, disabled: company?.disabled ?? false,
    // 1.3.1 (Q1): the quotas in GiB, empty for none.
    quotaMonth: toGib(company?.quotaBytesMonth), quotaPending: toGib(company?.quotaBytesPending),
  });
  const quotaOk = (s: string) => s.trim() === '' || fromGib(s) !== null;
  const [busy, setBusy] = useState(false);
  async function save() {
    setBusy(true);
    try {
      const body = {
        name: v.name.trim(), contactName: v.contactName.trim() || null, contactEmail: v.contactEmail.trim() || null, notes: v.notes, twoStepPolicy: v.twoStepPolicy,
        // Sent only when changed, so a quota that is not a round GiB figure is kept exactly.
        ...(!company || v.quotaMonth !== toGib(company.quotaBytesMonth) ? { quotaBytesMonth: fromGib(v.quotaMonth) } : {}),
        ...(!company || v.quotaPending !== toGib(company.quotaBytesPending) ? { quotaBytesPending: fromGib(v.quotaPending) } : {}),
      };
      const saved = company
        ? await api<PortalCompanyDTO>(`/api/v1/admin/portal/companies/${company.id}`, { method: 'PATCH', json: { ...body, disabled: v.disabled } })
        : await api<PortalCompanyDTO>('/api/v1/admin/portal/companies', { method: 'POST', json: body });
      await refresh();
      toast(company ? 'Company saved' : `${saved.name} added`);
      onSaved(saved);
    } catch (err) {
      toast(errorDetail(err), 'error');
      setBusy(false);
    }
  }
  return (
    <Modal title={company ? `Edit ${company.name}` : 'New company'} onClose={onClose} actions={<>
      <button className="btn" onClick={onClose}>Cancel</button>
      <button className="btn primary" disabled={busy || !v.name.trim() || !quotaOk(v.quotaMonth) || !quotaOk(v.quotaPending)} onClick={() => void save()} data-testid="portal-company-save">{company ? 'Save' : 'Add company'}</button>
    </>}>
      <div className="field"><label htmlFor="pc-name">Name</label><input id="pc-name" className="input" value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} data-testid="portal-company-name" /></div>
      <div className="field-row">
        <div className="field"><label htmlFor="pc-contact">Contact person</label><input id="pc-contact" className="input" value={v.contactName} onChange={(e) => setV({ ...v, contactName: e.target.value })} /></div>
        <div className="field"><label htmlFor="pc-email">Contact email</label><input id="pc-email" className="input" type="email" value={v.contactEmail} onChange={(e) => setV({ ...v, contactEmail: e.target.value })} /></div>
      </div>
      <div className="field">
        <label htmlFor="pc-2fa">Two-step sign-in</label>
        <select id="pc-2fa" className="select" value={v.twoStepPolicy} onChange={(e) => setV({ ...v, twoStepPolicy: e.target.value as CompanyTwoStepPolicy })} data-testid="portal-company-2fa">
          <option value="inherit">As the portal’s settings</option>
          <option value="required">Required for this company</option>
          <option value="optional">Optional for this company</option>
        </select>
        <span className="help">Required by default. Relax it only for a company that cannot use an authenticator app.</span>
      </div>
      <div className="field-row">
        <div className="field">
          <label htmlFor="pc-quota-month">Uploads per month (GiB)</label>
          <input id="pc-quota-month" className={`input ${quotaOk(v.quotaMonth) ? '' : 'invalid'}`} inputMode="decimal" placeholder="No quota" value={v.quotaMonth} onChange={(e) => setV({ ...v, quotaMonth: e.target.value })} data-testid="portal-company-quota-month" />
          <span className="help">What the company may upload in a calendar month, whatever becomes of it.</span>
        </div>
        <div className="field">
          <label htmlFor="pc-quota-pending">Waiting for review at once (GiB)</label>
          <input id="pc-quota-pending" className={`input ${quotaOk(v.quotaPending) ? '' : 'invalid'}`} inputMode="decimal" placeholder="No quota" value={v.quotaPending} onChange={(e) => setV({ ...v, quotaPending: e.target.value })} data-testid="portal-company-quota-pending" />
          <span className="help">Files not accepted or rejected yet. An upload that would pass either quota is refused; System status warns at 80 %.</span>
        </div>
      </div>
      <div className="field"><label htmlFor="pc-notes">Notes</label><textarea id="pc-notes" className="textarea" value={v.notes} onChange={(e) => setV({ ...v, notes: e.target.value })} placeholder="Only staff see these" /></div>
      {company && (
        <label className="toggle-row"><input type="checkbox" checked={v.disabled} onChange={(e) => setV({ ...v, disabled: e.target.checked })} data-testid="portal-company-disabled" />
          <span>Disabled: its users cannot sign in, and are signed out now</span></label>
      )}
    </Modal>
  );
}

function CompanyUsers({ company }: { company: PortalCompanyDTO }) {
  const users = usePortalUsers(company.id);
  const refresh = usePortalAdminRefresh();
  const [inviting, setInviting] = useState(false);
  const [link, setLink] = useState<PortalInviteResult | null>(null);
  const act = async (u: PortalUserDTO, path: string, done: string, confirmText?: string, json: unknown = {}, method = 'POST') => {
    if (confirmText && !window.confirm(confirmText)) return;
    try {
      const r = await api<PortalInviteResult & { sessionsEnded?: number }>(`/api/v1/admin/portal/users/${u.id}${path}`, { method, json });
      await refresh();
      if (path === '/invite') setLink(r); else toast(done);
    } catch (err) {
      toast(errorDetail(err), 'error');
    }
  };
  const forget = async (u: PortalUserDTO) => {
    const typed = window.prompt(`Remove ${u.displayName}’s personal data? The account closes; their name, email, password and two-step sign-in go, and their name is removed from the audit trail. Their deliveries stay. Type their email to confirm.`);
    if (typed?.trim().toLowerCase() !== u.email.toLowerCase()) return;
    await act(u, '/forget', `${u.displayName}’s personal data removed`);
  };
  const items = users.data?.items ?? [];
  return (
    <section className="panel-card portal-section" data-testid="portal-users">
      <div className="portal-card-head">
        <h3><Users size={16} color="var(--accent-strong)" /> Users of {company.name}</h3>
        <button className="btn sm" disabled={company.disabled} onClick={() => setInviting(true)} data-testid="portal-invite-user"><UserPlus size={14} /> Invite user</button>
      </div>
      <TableScroll>
        <table className="table compact">
          <thead><tr><th>Name</th><th>Status</th><th>Two-step</th><th>Last sign-in</th><th /></tr></thead>
          <tbody>
            {users.data && !items.length && <tr><td colSpan={5} className="muted">No users yet. Invite someone: they get an email with a link to choose their password (valid 24 hours).</td></tr>}
            {items.map((u) => (
              <tr key={u.id} className={u.status === 'disabled' ? 'faint' : undefined} data-portal-user={u.email}>
                <td className="wrap" style={{ minWidth: 170 }}><div style={{ fontWeight: 560 }}>{u.displayName}</div><div className="faint break-any">{u.email}</div></td>
                <td className="nowrap">
                  {u.status === 'active' && <span className="pill accent">Active</span>}
                  {u.status === 'disabled' && <span className="pill danger">Disabled</span>}
                  {u.status === 'invited' && <span className="pill warning" title={u.invitationExpiresAt ? `The link works until ${new Date(u.invitationExpiresAt).toLocaleString()}` : 'The invitation link has expired'}>
                    {u.invitationExpiresAt && new Date(u.invitationExpiresAt) > new Date() ? 'Invited' : 'Invitation expired'}</span>}
                </td>
                <td className="nowrap">{u.twoStepEnabled ? 'On' : 'Not set up'}</td>
                <td className="muted nowrap">{u.lastSignInAt ? relative(u.lastSignInAt) : 'Never'}</td>
                <td className="portal-actions"><div>
                  {u.status === 'invited' && <button className="btn ghost sm" onClick={() => void act(u, '/invite', '')} data-testid="portal-resend">Resend invitation</button>}
                  {u.twoStepEnabled && <button className="btn ghost sm" onClick={() => void act(u, '/reset-two-step', `Two-step sign-in reset for ${u.displayName}`, `Reset two-step sign-in for ${u.displayName}? Their authenticator and recovery codes stop working and they are signed out; they set it up again at their next sign-in.`)} data-testid="portal-reset-2fa">Reset two-step</button>}
                  {u.status === 'active' && <button className="btn ghost sm" onClick={() => void act(u, '/sign-out', `${u.displayName} is signed out everywhere`)} data-testid="portal-sign-out">Sign out everywhere</button>}
                  <button className="btn ghost sm" onClick={() => void act(u, '', u.status === 'disabled' ? `${u.displayName} enabled` : `${u.displayName} disabled`, undefined, { disabled: u.status !== 'disabled' }, 'PATCH')} data-testid="portal-toggle-user">{u.status === 'disabled' ? 'Enable' : 'Disable'}</button>
                  <a className="btn ghost sm" href={`/api/v1/admin/portal/users/${u.id}/personal-data`} download title="Everything PRISM keeps about this person, as JSON (GDPR)">Export data</a>
                  <button className="btn ghost sm" onClick={() => void forget(u)} title="GDPR: close the account and remove what identifies them">Remove data…</button>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableScroll>
      {inviting && <InviteModal company={company} onClose={() => setInviting(false)} onInvited={(r) => { setInviting(false); setLink(r); }} />}
      {link && (
        <Modal title="Invitation sent" onClose={() => setLink(null)} actions={<button className="btn primary" onClick={() => setLink(null)}>Done</button>}>
          <p className="muted" style={{ marginTop: 0 }} data-testid="portal-invite-result">
            {link.emailed ? `${link.user.email} gets an email with this link.` : `Email is not set up, so pass this link on to ${link.user.email} yourself.`} It works once, until {new Date(link.expiresAt).toLocaleString()}.
          </p>
          {link.inviteLink ? (
            <div style={{ display: 'flex', gap: 8 }}>
              <code className="code-block" style={{ flex: 1 }} data-testid="portal-invite-link">{link.inviteLink}</code>
              <button className="btn icon" aria-label="Copy the link" onClick={() => copy(link.inviteLink!)}><Copy size={14} /></button>
            </div>
          ) : <p className="muted">The portal has no address (PRISM_PORTAL_ORIGIN), so there is no link yet.</p>}
        </Modal>
      )}
    </section>
  );
}

function InviteModal({ company, onClose, onInvited }: { company: PortalCompanyDTO; onClose: () => void; onInvited: (r: PortalInviteResult) => void }) {
  const refresh = usePortalAdminRefresh();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  async function send() {
    setBusy(true);
    try {
      const r = await api<PortalInviteResult>(`/api/v1/admin/portal/companies/${company.id}/users`, { method: 'POST', json: { email: email.trim(), displayName: name.trim() } });
      await refresh();
      onInvited(r);
    } catch (err) {
      toast(errorDetail(err), 'error');
      setBusy(false);
    }
  }
  return (
    <Modal title={`Invite a user to ${company.name}`} onClose={onClose} actions={<>
      <button className="btn" onClick={onClose}>Cancel</button>
      <button className="btn primary" disabled={busy || !email.trim() || !name.trim()} onClick={() => void send()} data-testid="portal-invite-send">Send invitation</button>
    </>}>
      <div className="field"><label htmlFor="pi-name">Name</label><input id="pi-name" className="input" value={name} onChange={(e) => setName(e.target.value)} data-testid="portal-invite-name" /></div>
      <div className="field"><label htmlFor="pi-email">Email</label><input id="pi-email" className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="portal-invite-email" /></div>
      <p className="help" style={{ margin: 0 }}>They get a link to choose their password, valid 24 hours. Portal users are separate from PRISM’s members and never see PRISM itself.</p>
    </Modal>
  );
}

const STATE_LABEL: Record<string, string> = {
  draft: 'Uploading', waiting: 'Waiting for review', needs_changes: 'Needs changes', accepted: 'Accepted', rejected: 'Rejected', withdrawn: 'Withdrawn', expired: 'Expired',
};

function CompanyUploads({ company }: { company: PortalCompanyDTO }) {
  const q = usePortalCompanyUploads(company.id);
  const items = q.data?.items ?? [];
  return (
    <section className="panel-card portal-section" data-testid="portal-company-uploads">
      <h3>Uploads from {company.name}</h3>
      <TableScroll>
        <table className="table compact">
          <thead><tr><th>What</th><th>Kind</th><th>By</th><th>Files</th><th>State</th><th>Arrived</th></tr></thead>
          <tbody>
            {q.data && !items.length && <tr><td colSpan={6} className="muted">Nothing uploaded yet.</td></tr>}
            {items.map((s) => (
              <tr key={s.id}>
                <td className="wrap" style={{ minWidth: 180 }}>{s.title ?? '—'}{s.placeholderCode && <div className="faint mono">{s.placeholderCode}</div>}</td>
                <td className="nowrap">{s.kind === 'delivery' ? 'Delivery' : 'Open upload'}</td>
                <td className="wrap">{s.user ?? '—'}</td>
                <td className="nowrap">{s.files} · {bytes(s.bytes)}</td>
                <td className="nowrap">{STATE_LABEL[s.state] ?? s.state}</td>
                <td className="muted nowrap">{relative(s.submittedAt ?? s.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableScroll>
    </section>
  );
}
